import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  GraduationCap, 
  CheckCircle2, 
  Camera, 
  FileCheck, 
  User, 
  Mic,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Shield,
  Scan,
  UserCheck,
  Volume2,
  RefreshCw,
  Upload,
  Eye,
  Fingerprint,
  Play,
  Square,
  Video,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as faceapi from 'face-api.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONSENT_STATEMENT = `I solemnly declare that I am the person whose identity has been verified. I understand that this interview is being recorded and will be analyzed. I acknowledge that any form of cheating, impersonation, or fraudulent activity will result in immediate disqualification and may have legal consequences. I confirm that I will answer all questions honestly and to the best of my knowledge.`;

const STEPS = [
  { id: 1, title: 'Verify Identity', description: 'Enter your reference numbers', icon: User },
  { id: 2, title: 'Select University', description: 'Confirm your university', icon: GraduationCap },
  { id: 3, title: 'Upload Document', description: 'Upload ID for face reference', icon: FileCheck },
  { id: 4, title: 'Face Match', description: 'Verify your identity', icon: Fingerprint },
  { id: 5, title: 'Liveness Check', description: 'Confirm you are human', icon: Eye },
  { id: 6, title: 'Consent & Voice', description: 'Accept terms and record voice', icon: Volume2 },
];

export default function InterviewVerification() {
  const { token } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [applicationData, setApplicationData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form states
  const [identityForm, setIdentityForm] = useState({
    university_reference_number: '',
    passport_number: '',
    nic_number: ''
  });
  const [universityForm, setUniversityForm] = useState({
    university_id: '',
    campus: ''
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  
  // Document & Reference states
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [referenceDescriptor, setReferenceDescriptor] = useState(null);
  const [referenceFaceImage, setReferenceFaceImage] = useState(null);
  const [documentType, setDocumentType] = useState('passport');
  const [documentVerification, setDocumentVerification] = useState(null);
  const [isExtractingDocument, setIsExtractingDocument] = useState(false);
  
  // Face matching states
  const [capturedSelfie, setCapturedSelfie] = useState(null);
  const [faceMatchScore, setFaceMatchScore] = useState(null);
  const [faceMatchStatus, setFaceMatchStatus] = useState('waiting'); // waiting, detecting, matching, matched, failed
  const [isCapturing, setIsCapturing] = useState(false);
  const [faceMatchThreshold, setFaceMatchThreshold] = useState(60); // Admin configurable
  const faceMatchScoreRef = useRef(0); // Ref to avoid stale closure in callbacks
  
  // Liveness states
  const [livenessChecks, setLivenessChecks] = useState({
    straight: false,
    left: false,
    right: false
  });
  const [currentLivenessAction, setCurrentLivenessAction] = useState('straight');
  const [headPose, setHeadPose] = useState({ yaw: 0, pitch: 0 });
  
  // Face detection states
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [faceDetectionFailed, setFaceDetectionFailed] = useState(false);
  const [facePosition, setFacePosition] = useState('none');
  const detectionIntervalRef = useRef(null);
  const autoMatchTimeoutRef = useRef(null);

  // Consent recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(30);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const chunksRef = useRef([]);

  // Load face-api models with multiple source fallback
  useEffect(() => {
    const loadModels = async () => {
      // Multiple model sources in order of preference
      const modelSources = [
        // 1. Official face-api.js GitHub CDN (jsDelivr) - most reliable
        'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js/weights',
        // 2. Backend API fallback
        `${API_URL}/api/models`
      ];
      
      for (let i = 0; i < modelSources.length; i++) {
        const modelPath = modelSources[i];
        console.log(`Attempting to load face-api models from: ${modelPath} (source ${i + 1}/${modelSources.length})`);
        
        try {
          // First verify the models are accessible
          const testFetch = await fetch(`${modelPath}/tiny_face_detector_model-weights_manifest.json`);
          if (!testFetch.ok) {
            throw new Error(`Models not accessible: ${testFetch.status}`);
          }
          console.log('Model manifest accessible, loading models...');
          
          // Load models sequentially to better handle errors
          console.log('Loading tiny face detector...');
          await faceapi.nets.tinyFaceDetector.loadFromUri(modelPath);
          
          console.log('Loading face landmark model...');
          await faceapi.nets.faceLandmark68Net.loadFromUri(modelPath);
          
          console.log('Loading face recognition model...');
          await faceapi.nets.faceRecognitionNet.loadFromUri(modelPath);
          
          setModelsLoaded(true);
          setFaceDetectionFailed(false);
          console.log('All face detection models loaded successfully from:', modelPath);
          return; // Success - exit the function
        } catch (err) {
          console.error(`Failed to load models from ${modelPath}:`, err.message || err);
          
          if (i < modelSources.length - 1) {
            console.log('Trying next model source...');
          }
        }
      }
      
      // All sources failed
      console.error('All model sources failed. Proceeding without face detection.');
      toast.warning('Face detection unavailable. You can still proceed with the interview.', {
        duration: 5000
      });
      setFaceDetectionFailed(true);
      setModelsLoaded(true); // Set true to not block the user
    };
    
    // Small delay to ensure page is fully loaded
    const timer = setTimeout(() => loadModels(), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    verifyToken();
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      clearAllIntervals();
    };
  }, [token]);

  const clearAllIntervals = () => {
    if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
    if (autoMatchTimeoutRef.current) clearTimeout(autoMatchTimeoutRef.current);
  };

  // Auto-start camera for steps 4, 5, and 6
  useEffect(() => {
    if ((currentStep === 4 || currentStep === 5 || currentStep === 6) && !cameraStream && !cameraError) {
      startCamera();
    }
  }, [currentStep]);

  // Reconnect video stream when step changes
  useEffect(() => {
    if ((currentStep === 4 || currentStep === 5 || currentStep === 6) && cameraStream && videoRef.current) {
      const timer = setTimeout(() => {
        if (videoRef.current && cameraStream) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.play().catch(e => console.log('Play error:', e));
          console.log('Reconnected video for step', currentStep);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, cameraStream]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/interview/verify/${token}`);
      setApplicationData(response.data);
      setUniversityForm(prev => ({ ...prev, university_id: response.data.university_id }));
      // Load admin-configured face match threshold
      if (response.data.face_match_threshold) {
        setFaceMatchThreshold(response.data.face_match_threshold);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired interview link');
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }
      });
      
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      toast.success('Camera connected!');
      return stream;
    } catch (error) {
      const msg = error.name === 'NotAllowedError' ? 'Please allow camera access' :
                  error.name === 'NotFoundError' ? 'No camera found' : 'Camera error';
      setCameraError(msg);
      toast.error(msg);
      return null;
    }
  };

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  }, []);

  // Step handlers
  const handleStep1 = async () => {
    if (!identityForm.university_reference_number) {
      toast.error('Please enter University Reference Number');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/interview/${token}/verify-identity`, identityForm);
      toast.success('Identity verified!');
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleStep2 = async () => {
    if (!universityForm.university_id) {
      toast.error('Please select university');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/interview/${token}/verify-university`, universityForm);
      toast.success('University verified!');
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Auto-align/rotate image based on face detection
  const autoAlignImage = async (imageData) => {
    return new Promise(async (resolve) => {
      try {
        const img = new Image();
        img.onload = async () => {
          // Use standard detector options for passport photos
          const detectorOptions = new faceapi.TinyFaceDetectorOptions({
            inputSize: 512,
            scoreThreshold: 0.3
          });
          
          console.log(`[Auto-Align] Image dimensions: ${img.width}x${img.height}`);
          
          // Try to detect face in original orientation
          let detection = await faceapi.detectSingleFace(img, detectorOptions)
            .withFaceLandmarks();
          
          if (detection) {
            console.log('[Auto-Align] Face found in original orientation');
            // Face found - check if it needs tilt correction based on eye positions
            const landmarks = detection.landmarks;
            const leftEye = landmarks.getLeftEye();
            const rightEye = landmarks.getRightEye();
            
            const leftEyeCenter = {
              x: leftEye.reduce((sum, p) => sum + p.x, 0) / leftEye.length,
              y: leftEye.reduce((sum, p) => sum + p.y, 0) / leftEye.length
            };
            const rightEyeCenter = {
              x: rightEye.reduce((sum, p) => sum + p.x, 0) / rightEye.length,
              y: rightEye.reduce((sum, p) => sum + p.y, 0) / rightEye.length
            };
            
            const deltaY = rightEyeCenter.y - leftEyeCenter.y;
            const deltaX = rightEyeCenter.x - leftEyeCenter.x;
            const angleRadians = Math.atan2(deltaY, deltaX);
            const angleDegrees = angleRadians * (180 / Math.PI);
            
            // Only correct if tilt is significant (> 5 degrees) but not extreme (< 45 degrees)
            // Extreme angles indicate a false detection, not a real tilt
            if (Math.abs(angleDegrees) > 5 && Math.abs(angleDegrees) < 45) {
              console.log(`[Auto-Align] Correcting tilt: ${angleDegrees.toFixed(1)}°`);
              toast.info(`Correcting image tilt (${Math.abs(angleDegrees).toFixed(0)}°)...`);
              
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const radians = -angleRadians;
              const cos = Math.abs(Math.cos(radians));
              const sin = Math.abs(Math.sin(radians));
              canvas.width = img.width * cos + img.height * sin;
              canvas.height = img.width * sin + img.height * cos;
              ctx.translate(canvas.width / 2, canvas.height / 2);
              ctx.rotate(radians);
              ctx.drawImage(img, -img.width / 2, -img.height / 2);
              
              resolve(canvas.toDataURL('image/jpeg', 0.95));
              return;
            }
            
            // Face found, no significant tilt - return original
            resolve(imageData);
            return;
          }
          
          // No face found in original — do NOT auto-rotate
          // Passport images from modern phones/scanners have correct EXIF orientation 
          // that browsers auto-apply. Aggressive rotation causes more harm than good
          // by producing false-positive face detections on rotated text/patterns.
          console.log('[Auto-Align] No face detected in original orientation - keeping as-is');
          resolve(imageData);
        };
        
        img.onerror = () => {
          console.error('[Auto-Align] Failed to load image');
          resolve(imageData);
        };
        
        img.src = imageData;
      } catch (error) {
        console.error('[Auto-Align] Error:', error);
        resolve(imageData);
      }
    });
  };

  // Document upload and face extraction
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      let rawImageData = event.target.result;
      setIsExtractingDocument(true);
      setDocumentVerification(null);
      
      // Check if uploaded file is a PDF — convert to image via backend
      const isPDF = file.type === 'application/pdf' || rawImageData.startsWith('data:application/pdf');
      if (isPDF) {
        toast.info('Converting PDF to image...');
        try {
          const convertResponse = await axios.post(`${API_URL}/api/interview/${token}/convert-pdf-to-image`, {
            pdf_data: rawImageData
          }, { timeout: 60000 });
          if (convertResponse.data.success && convertResponse.data.image_data) {
            rawImageData = convertResponse.data.image_data;
            toast.success('PDF converted successfully!');
          } else {
            toast.error('Failed to convert PDF. Please upload an image instead.');
            setIsExtractingDocument(false);
            return;
          }
        } catch (pdfErr) {
          console.error('PDF conversion error:', pdfErr);
          toast.error(pdfErr.response?.data?.detail || 'Failed to convert PDF. Please upload a clear image of your passport instead.');
          setIsExtractingDocument(false);
          return;
        }
      }
      
      // Step 0: Auto-align/rotate the image if needed
      toast.info('Checking image alignment...');
      const imageData = await autoAlignImage(rawImageData);
      
      setUploadedDocument(imageData);
      
      // Step 1: Extract face from document using face-api
      toast.info('Processing document...');
      try {
        const img = await faceapi.fetchImage(imageData);
        
        // Use sensitive detector options for passport photos
        const detectorOptions = new faceapi.TinyFaceDetectorOptions({
          inputSize: 512,
          scoreThreshold: 0.2
        });
        
        let detection = await faceapi.detectSingleFace(img, detectorOptions)
          .withFaceLandmarks()
          .withFaceDescriptor();
        
        // If no detection, try with even lower threshold
        if (!detection) {
          const looseOptions = new faceapi.TinyFaceDetectorOptions({
            inputSize: 608,
            scoreThreshold: 0.1
          });
          detection = await faceapi.detectSingleFace(img, looseOptions)
            .withFaceLandmarks()
            .withFaceDescriptor();
        }
        
        if (detection) {
          setReferenceDescriptor(detection.descriptor);
          
          // Extract face region for display
          const { x, y, width, height } = detection.detection.box;
          const canvas = document.createElement('canvas');
          const padding = 40;
          canvas.width = width + padding * 2;
          canvas.height = height + padding * 2;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, x - padding, y - padding, width + padding * 2, height + padding * 2, 0, 0, canvas.width, canvas.height);
          setReferenceFaceImage(canvas.toDataURL('image/jpeg', 0.9));
          
          toast.success('Face extracted!');
        } else {
          toast.error('No face detected. Please upload a clearer image.');
          setUploadedDocument(null);
          setIsExtractingDocument(false);
          return;
        }
        
        // Step 2: Extract document information using AI Vision
        toast.info('Analyzing document with AI... This may take up to 30 seconds.');
        try {
          const extractResponse = await axios.post(`${API_URL}/api/interview/${token}/extract-document`, {
            document_type: documentType,
            document_image: imageData
          }, {
            timeout: 90000 // 90 second timeout for AI processing
          });
          
          setDocumentVerification(extractResponse.data);
          
          if (extractResponse.data.document_verified) {
            toast.success('Document verified successfully!');
          } else {
            const matching = extractResponse.data.matching;
            if (!matching.name.match) {
              toast.error(`Name mismatch: Expected "${matching.name.expected}", found "${matching.name.extracted || 'Not detected'}"`);
            }
            if (documentType === 'passport' && matching.passport_number.expected && !matching.passport_number.match) {
              toast.error(`Passport number mismatch`);
            }
            if (documentType === 'nic' && matching.nic_number.expected && !matching.nic_number.match) {
              toast.error(`NIC number mismatch`);
            }
          }
        } catch (extractErr) {
          console.error('Document extraction error:', extractErr);
          if (extractErr.code === 'ECONNABORTED') {
            toast.error('Document analysis timed out. Please try again with a clearer image.');
          } else if (extractErr.response?.data?.detail) {
            toast.error(extractErr.response.data.detail);
          } else {
            toast.error('Failed to extract document information. Please try again.');
          }
        }
        
      } catch (err) {
        console.error('Document processing error:', err);
        toast.error('Failed to process document');
      } finally {
        setIsExtractingDocument(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleStep3 = async () => {
    if (!uploadedDocument || !referenceDescriptor) {
      toast.error('Please upload a document with a clear face photo');
      return;
    }
    if (!documentVerification?.document_verified) {
      toast.error('Document verification failed. Please upload a valid document that matches your application.');
      return;
    }
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/interview/${token}/upload-document`, {
        document_type: documentType,
        document_data: uploadedDocument.substring(0, 100) + '...',
        extracted_info: documentVerification?.extracted_info,
        verification_result: documentVerification
      });
      toast.success('Document verified!');
      setCurrentStep(4);
      setCapturedSelfie(null);
      setFaceMatchStatus('waiting');
      startCamera();
    } catch (error) {
      toast.error('Document verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Face matching detection (Step 4)
  useEffect(() => {
    if (currentStep === 4 && modelsLoaded && cameraStream && !capturedSelfie && referenceDescriptor) {
      setFaceMatchStatus('detecting');
      
      detectionIntervalRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState !== 4) return;
        
        try {
          const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks()
            .withFaceDescriptor();
          
          if (detection) {
            const videoWidth = videoRef.current.videoWidth;
            const videoHeight = videoRef.current.videoHeight;
            const box = detection.detection.box;
            const centerX = videoWidth / 2;
            const centerY = videoHeight / 2;
            const faceCenterX = box.x + box.width / 2;
            const faceCenterY = box.y + box.height / 2;
            
            const xOffset = Math.abs(faceCenterX - centerX) / videoWidth;
            const yOffset = Math.abs(faceCenterY - centerY) / videoHeight;
            const faceRatio = box.height / videoHeight;
            
            const isCentered = xOffset < 0.15 && yOffset < 0.15;
            const isGoodSize = faceRatio > 0.25 && faceRatio < 0.65;
            
            if (isCentered && isGoodSize) {
              setFacePosition('perfect');
              setFaceMatchStatus('matching');
              
              // Calculate match score using client-side face-api.js
              const distance = faceapi.euclideanDistance(referenceDescriptor, detection.descriptor);
              const score = Math.max(0, Math.min(100, (1 - distance) * 100));
              setFaceMatchScore(Math.round(score));
              faceMatchScoreRef.current = Math.round(score); // Keep ref in sync for callback access
              
              // Auto-capture when face is centered, good size, and score is reasonable
              // Score >= 35% indicates same person with different conditions (lighting, angle)
              if (score >= 35 && !autoMatchTimeoutRef.current) {
                autoMatchTimeoutRef.current = setTimeout(() => {
                  autoCaptureFace();
                }, 1500);
              }
            } else {
              setFacePosition('partial');
              setFaceMatchStatus('detecting');
              if (autoMatchTimeoutRef.current) {
                clearTimeout(autoMatchTimeoutRef.current);
                autoMatchTimeoutRef.current = null;
              }
            }
          } else {
            setFacePosition('none');
            setFaceMatchStatus('detecting');
          }
        } catch (err) {
          console.log('Detection error:', err);
        }
      }, 200);
      
      return () => clearAllIntervals();
    }
  }, [currentStep, modelsLoaded, cameraStream, capturedSelfie, referenceDescriptor]);

  const autoCaptureFace = async () => {
    if (capturedSelfie || isCapturing) return;
    setIsCapturing(true);
    
    const imageData = captureFrame();
    if (imageData) {
      setCapturedSelfie(imageData);
      setFaceMatchStatus('verifying');
      clearAllIntervals();
      
      // Try AWS Rekognition first (enterprise-grade accuracy)
      let verificationResult = null;
      let usedMethod = 'face-api.js';
      
      try {
        toast.info('Verifying identity with AWS Rekognition...');
        
        const awsResponse = await axios.post(`${API_URL}/api/interview/${token}/verify-face-aws`, {
          document_image: uploadedDocument,
          selfie_image: imageData,
          similarity_threshold: faceMatchThreshold  // Use admin-configured threshold
        }, {
          timeout: 30000  // 30 second timeout
        });
        
        if (awsResponse.data.success) {
          verificationResult = {
            passed: awsResponse.data.verified,
            score: awsResponse.data.match_score,
            confidence: awsResponse.data.confidence,
            method: 'aws_rekognition'
          };
          usedMethod = 'aws_rekognition';
          console.log('[AWS Rekognition] Result:', verificationResult);
        } else {
          // AWS returned an error - check if it's a face detection issue
          console.warn('[AWS Rekognition] Error:', awsResponse.data.error);
          if (awsResponse.data.error === 'no_face_detected') {
            toast.warning('Could not detect face clearly. Trying alternative method...');
          }
        }
      } catch (awsError) {
        console.error('[AWS Rekognition] API Error:', awsError);
        toast.warning('AWS verification unavailable. Using fallback method...');
      }
      
      // Fallback to face-api.js if AWS didn't work
      if (!verificationResult) {
        const currentScore = faceMatchScoreRef.current || 0; // Use ref to avoid stale closure
        verificationResult = {
          passed: currentScore >= (faceMatchThreshold * 0.65), // face-api.js scores are lower, scale threshold
          score: currentScore,
          confidence: currentScore >= 60 ? 'high' : currentScore >= 45 ? 'medium' : 'low',
          method: 'face-api.js'
        };
        usedMethod = 'face-api.js';
        console.log('[face-api.js] Fallback result:', verificationResult);
      }
      
      // Store the verification attempt for admin review
      try {
        await axios.post(`${API_URL}/api/interview/${token}/verify-face`, {
          selfie_data: imageData?.substring(0, 100) + '...',
          match_score: verificationResult.score / 100,
          passed: verificationResult.passed,
          reference_face_descriptor: referenceDescriptor ? Array.from(referenceDescriptor) : null,
          selfie_image_full: imageData, // Full selfie for AWS face swap detection during interview
          verification_method: usedMethod,
          threshold_used: faceMatchThreshold
        });
      } catch (e) {
        console.error('Failed to save verification attempt:', e);
      }
      
      // Process verification result
      if (verificationResult.passed) {
        // Face matched successfully
        const methodLabel = usedMethod === 'aws_rekognition' ? 'AWS Rekognition' : 'face-api.js';
        toast.success(`Face verified! (${verificationResult.score.toFixed(1)}% match via ${methodLabel})`);
        setFaceMatchStatus('matched');
        setFaceMatchScore(Math.round(verificationResult.score));
        
        // Auto proceed after 2 seconds
        setTimeout(() => {
          handleStep4Success();
        }, 2000);
      } else if (verificationResult.score >= (faceMatchThreshold * 0.75)) {
        // Borderline case - score is close to threshold, allow but flag for review
        toast.warning(`Face verified with low confidence (${verificationResult.score.toFixed(1)}%). This will be flagged for admin review.`);
        setFaceMatchStatus('matched');
        setFaceMatchScore(Math.round(verificationResult.score));
        
        // Mark as low confidence for admin review
        try {
          await axios.post(`${API_URL}/api/interview/${token}/flag-verification`, {
            reason: 'low_face_match_score',
            score: verificationResult.score,
            threshold: faceMatchThreshold,
            method: usedMethod
          });
        } catch (e) {
          console.error('Failed to flag verification:', e);
        }
        
        setTimeout(() => {
          handleStep4Success();
        }, 2000);
      } else {
        // Score too low - likely different person
        setFaceMatchStatus('failed');
        setFaceMatchScore(Math.round(verificationResult.score));
        toast.error(`Face verification failed (${verificationResult.score.toFixed(1)}% match). The faces don't appear to match. Please ensure you are the same person as in the document.`);
        
        // Allow retry after 3 seconds
        setTimeout(() => {
          setCapturedSelfie(null);
          setFaceMatchStatus('detecting');
        }, 3000);
      }
    }
    setIsCapturing(false);
  };

  const handleStep4Success = async () => {
    setVerifying(true);
    try {
      toast.success('Face verification passed!');
      setCurrentStep(5);
      setLivenessChecks({ straight: false, left: false, right: false });
      setCurrentLivenessAction('straight');
      
      // Force reconnect video after step change
      setTimeout(() => {
        if (videoRef.current && cameraStream) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.play().catch(e => console.log('Play after step change:', e));
        }
      }, 200);
    } catch (error) {
      toast.error('Failed to proceed');
    } finally {
      setVerifying(false);
    }
  };

  // Liveness detection (Step 5) - Auto detect head movements
  useEffect(() => {
    if (currentStep === 5 && modelsLoaded && cameraStream) {
      detectionIntervalRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState !== 4) return;
        
        try {
          // Use lower threshold for better detection
          const detectorOptions = new faceapi.TinyFaceDetectorOptions({
            inputSize: 416,
            scoreThreshold: 0.3
          });
          
          const detection = await faceapi.detectSingleFace(videoRef.current, detectorOptions)
            .withFaceLandmarks();
          
          if (detection) {
            const landmarks = detection.landmarks;
            const box = detection.detection.box;
            const nose = landmarks.getNose();
            const leftEye = landmarks.getLeftEye();
            const rightEye = landmarks.getRightEye();
            
            // Get nose tip position
            const noseTip = nose[3];
            
            // Get eye centers for better reference
            const leftEyeCenter = leftEye.reduce((acc, p) => ({ x: acc.x + p.x / leftEye.length, y: acc.y + p.y / leftEye.length }), { x: 0, y: 0 });
            const rightEyeCenter = rightEye.reduce((acc, p) => ({ x: acc.x + p.x / rightEye.length, y: acc.y + p.y / rightEye.length }), { x: 0, y: 0 });
            const eyesMidX = (leftEyeCenter.x + rightEyeCenter.x) / 2;
            const eyeDistance = Math.abs(rightEyeCenter.x - leftEyeCenter.x);
            
            // Calculate nose offset from eyes center (normalized by eye distance)
            // NOTE: Webcam is usually MIRRORED, so:
            // - When user turns head LEFT (their left), nose appears to move RIGHT on screen
            // - noseOffset > 0 means nose is RIGHT of eyes on screen = user turned LEFT
            // - noseOffset < 0 means nose is LEFT of eyes on screen = user turned RIGHT
            const noseOffset = (noseTip.x - eyesMidX) / eyeDistance;
            
            // Check eye width ratio as secondary indicator
            // When turning LEFT: right eye appears smaller (further from camera)
            // When turning RIGHT: left eye appears smaller
            const leftEyeWidth = Math.abs(leftEye[3].x - leftEye[0].x);
            const rightEyeWidth = Math.abs(rightEye[3].x - rightEye[0].x);
            const eyeWidthRatio = leftEyeWidth / (rightEyeWidth + 0.001);
            
            // Determine head direction based on nose offset
            // Thresholds are very low (0.08) because nose offset is subtle
            let direction = 'straight';
            if (noseOffset > 0.08 || eyeWidthRatio < 0.85) {
              direction = 'left';  // User's head turned to THEIR left
            } else if (noseOffset < -0.08 || eyeWidthRatio > 1.15) {
              direction = 'right'; // User's head turned to THEIR right
            }
            
            console.log('[Liveness] Head detection:', {
              noseOffset: noseOffset.toFixed(3),
              eyeWidthRatio: eyeWidthRatio.toFixed(3),
              direction: direction.toUpperCase(),
              checks: livenessChecks
            });
            
            setHeadPose({ yaw: noseOffset * 100, pitch: 0 });
            
            // Update liveness checks based on detected direction
            setLivenessChecks(prev => {
              const newChecks = { ...prev };
              
              // Looking straight
              if (direction === 'straight' && !prev.straight) {
                newChecks.straight = true;
                if (currentLivenessAction === 'straight') {
                  toast.success('✓ Looking straight detected!');
                  setTimeout(() => setCurrentLivenessAction('left'), 500);
                }
              }
              
              // Looking left (user turns their head to their left)
              if (direction === 'left' && !prev.left && prev.straight) {
                newChecks.left = true;
                if (currentLivenessAction === 'left') {
                  toast.success('✓ Turn left detected!');
                  setTimeout(() => setCurrentLivenessAction('right'), 500);
                }
              }
              
              // Looking right (user turns their head to their right)
              if (direction === 'right' && !prev.right && prev.straight && prev.left) {
                newChecks.right = true;
                if (currentLivenessAction === 'right') {
                  toast.success('✓ Turn right detected!');
                  setTimeout(() => setCurrentLivenessAction('done'), 500);
                }
              }
              
              return newChecks;
            });
          }
        } catch (err) {
          console.log('Liveness detection error:', err);
        }
      }, 200); // Check every 200ms
      
      return () => clearAllIntervals();
    }
  }, [currentStep, modelsLoaded, cameraStream, currentLivenessAction]);

  // Auto-proceed when all liveness checks complete
  useEffect(() => {
    if (livenessChecks.straight && livenessChecks.left && livenessChecks.right && currentStep === 5) {
      setTimeout(() => {
        handleStep5Auto();
      }, 1500);
    }
  }, [livenessChecks, currentStep]);

  const handleStep5Auto = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API_URL}/api/interview/${token}/liveness-check`, {
        liveness_passed: true,
        checks: livenessChecks
      });
      toast.success('Liveness check passed!');
      setCurrentStep(6);
      // Restart camera for consent recording
      setTimeout(() => {
        if (videoRef.current && cameraStream) {
          videoRef.current.srcObject = cameraStream;
          videoRef.current.play().catch(e => console.log('Play error:', e));
        }
      }, 200);
    } catch (error) {
      toast.error('Liveness check failed');
    } finally {
      setVerifying(false);
    }
  };

  // Start consent recording
  const startRecording = async () => {
    try {
      // Ensure we have camera stream with audio
      let stream = cameraStream;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true 
        });
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      }
      
      // Check if stream has audio
      if (stream.getAudioTracks().length === 0) {
        // Try to get audio
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.getAudioTracks().forEach(track => stream.addTrack(track));
      }
      
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9,opus'
      });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        toast.success('Recording saved! Click "Upload & Continue" to proceed.');
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(30);
      
      // Countdown timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast.info('Recording started! Read the statement aloud.');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  // Stop consent recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    setIsRecording(false);
  };

  // Upload recording and proceed
  const uploadAndProceed = async () => {
    if (!recordedBlob) {
      toast.error('Please record your consent statement first');
      return;
    }
    
    setIsUploading(true);
    try {
      // Convert blob to base64 for upload
      const reader = new FileReader();
      reader.readAsDataURL(recordedBlob);
      
      await new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            // Upload full audio data to backend (will be uploaded to Google Drive)
            await axios.post(`${API_URL}/api/interview/${token}/upload-consent`, {
              recording_data: reader.result, // Full base64 audio data
              duration: 30 - recordingTime,
              timestamp: new Date().toISOString()
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
      });

      await axios.post(`${API_URL}/api/interview/${token}/consent-voice`, {
        consent_given: true,
        voice_sample_recorded: true,
        recording_uploaded: true
      });
      
      setUploadComplete(true);
      toast.success('Consent recording uploaded successfully!');
      
      // Clean up and navigate
      if (cameraStream) cameraStream.getTracks().forEach(track => track.stop());
      setTimeout(() => navigate(`/interview/${token}/room`), 2000);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload recording. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleStep6 = async () => {
    if (!recordedBlob) {
      toast.error('Please record your consent statement first');
      return;
    }
    await uploadAndProceed();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-lime-500 mx-auto mb-4" />
          <p className="text-slate-300">Verifying interview link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-3">Invalid Link</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">Return Home</Button>
        </div>
      </div>
    );
  }

  const progress = (currentStep / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <canvas ref={canvasRef} className="hidden" />
      <input type="file" ref={fileInputRef} accept="image/*,.pdf,application/pdf" onChange={handleDocumentUpload} className="hidden" />
      
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="w-full px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-400 to-lime-600 flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="VisaInterviews.ai" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <span className="font-bold text-xl text-white">Visa<span className="text-lime-400">interviews</span>.ai</span>
              <p className="text-xs text-slate-400">AI Verification</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-medium text-white">{applicationData?.university_name}</p>
            <p className="text-sm text-slate-400">Credibility Interview</p>
          </div>
        </div>
      </header>

      <div className="w-full px-6 lg:px-12 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-3">
            <span className="text-slate-400">Step {currentStep} of {STEPS.length}</span>
            <span className="text-lime-400 font-medium">{Math.round(progress)}% Complete</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-lime-500 to-green-400 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="grid grid-cols-6 gap-2 mb-10">
          {STEPS.map((step) => (
            <div key={step.id} className={`relative p-3 rounded-xl transition-all ${
              step.id < currentStep ? 'bg-lime-500/20 border border-lime-500/30' :
              step.id === currentStep ? 'bg-slate-800 border-2 border-lime-500 shadow-lg shadow-lime-500/20' :
              'bg-slate-800/50 border border-slate-700'
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  step.id < currentStep ? 'bg-lime-500 text-white' :
                  step.id === currentStep ? 'bg-lime-500/20 text-lime-400' :
                  'bg-slate-700 text-slate-500'
                }`}>
                  {step.id < currentStep ? <CheckCircle2 className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                </div>
                <div className="hidden xl:block">
                  <p className={`text-xs font-medium ${step.id <= currentStep ? 'text-white' : 'text-slate-500'}`}>{step.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Visual */}
          <div className="order-2 lg:order-1">
            {/* Step 3: Document Upload Preview */}
            {currentStep === 3 && (
              <div className="relative aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-slate-700">
                {uploadedDocument ? (
                  <div className="relative w-full h-full">
                    <img src={uploadedDocument} alt="Document" className="w-full h-full object-contain" />
                    {referenceFaceImage && (
                      <div className="absolute bottom-4 right-4 p-2 bg-slate-900/90 rounded-xl border border-lime-500">
                        <p className="text-xs text-lime-400 mb-2 text-center">Reference Face</p>
                        <img src={referenceFaceImage} alt="Face" className="w-24 h-24 rounded-lg object-cover" />
                        <CheckCircle2 className="absolute -top-2 -right-2 w-6 h-6 text-lime-500 bg-slate-900 rounded-full" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full p-8">
                    <div className="w-24 h-24 rounded-full bg-slate-700/50 flex items-center justify-center mb-6 border-2 border-dashed border-slate-600">
                      <Upload className="w-10 h-10 text-slate-500" />
                    </div>
                    <p className="text-slate-400 text-center mb-4">Upload your Passport or NIC</p>
                    <p className="text-slate-500 text-sm text-center">Your face will be extracted as reference for verification</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4, 5 & 6: Camera View */}
            {(currentStep === 4 || currentStep === 5 || currentStep === 6) && (
              <div className="relative aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-slate-700">
                {cameraError ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Camera className="w-16 h-16 text-slate-600 mb-4" />
                    <p className="text-slate-400 mb-4">{cameraError}</p>
                    <Button onClick={startCamera} className="bg-lime-500 hover:bg-lime-600">Enable Camera</Button>
                  </div>
                ) : !cameraStream ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Loader2 className="w-16 h-16 text-lime-500 animate-spin mb-4" />
                    <p className="text-slate-300">Starting camera...</p>
                  </div>
                ) : (
                  <>
                    {currentStep === 4 && capturedSelfie ? (
                      <img src={capturedSelfie} alt="Captured" className="w-full h-full object-cover" />
                    ) : (
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        className="w-full h-full object-cover"
                        onLoadedMetadata={(e) => {
                          e.target.play().catch(err => console.log('Play error:', err));
                        }}
                      />
                    )}
                    
                    {/* Step 4: Face Match Overlay */}
                    {currentStep === 4 && !capturedSelfie && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="relative">
                          {/* Futuristic oval frame */}
                          <div className={`w-56 h-72 rounded-full border-4 transition-all duration-300 ${
                            facePosition === 'perfect' ? 'border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.5)]' :
                            facePosition === 'partial' ? 'border-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.3)]' :
                            'border-cyan-500/50'
                          }`}>
                            {/* Scanning animation */}
                            <div className="absolute inset-0 rounded-full overflow-hidden">
                              <div className={`absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-scan ${
                                facePosition === 'perfect' ? 'via-green-400' : facePosition === 'partial' ? 'via-yellow-400' : ''
                              }`} style={{ animation: 'scan 2s linear infinite' }} />
                            </div>
                            {/* Corner markers */}
                            <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 border-cyan-400 rounded-tl-lg" />
                            <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 border-cyan-400 rounded-tr-lg" />
                            <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 border-cyan-400 rounded-bl-lg" />
                            <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 border-cyan-400 rounded-br-lg" />
                          </div>
                          
                          {/* Match score display */}
                          {faceMatchScore !== null && facePosition === 'perfect' && (
                            <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-slate-900/90 px-4 py-2 rounded-xl border border-green-500">
                              <p className="text-green-400 text-lg font-bold">{faceMatchScore}% Match</p>
                            </div>
                          )}
                          
                          {/* Status message */}
                          <div className={`absolute -bottom-14 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full whitespace-nowrap ${
                            facePosition === 'perfect' ? 'bg-green-500 text-white' :
                            facePosition === 'partial' ? 'bg-yellow-500 text-black' :
                            'bg-cyan-500/80 text-white'
                          }`}>
                            {facePosition === 'perfect' ? '✓ Hold still - Matching...' :
                             facePosition === 'partial' ? 'Center your face' :
                             modelsLoaded ? 'Position face in oval' : 'Loading...'}
                          </div>
                        </div>
                        
                        {/* Reference face comparison */}
                        {referenceFaceImage && (
                          <div className="absolute top-4 left-4 bg-slate-900/90 p-2 rounded-xl border border-slate-600">
                            <p className="text-xs text-slate-400 mb-1">Reference</p>
                            <img src={referenceFaceImage} alt="Reference" className="w-16 h-16 rounded-lg object-cover" />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Step 4: Match result overlay */}
                    {currentStep === 4 && capturedSelfie && (
                      <div className={`absolute inset-0 ${faceMatchStatus === 'failed' ? 'bg-black/60' : 'bg-black/50'} flex items-center justify-center`}>
                        <div className="text-center">
                          {faceMatchStatus === 'failed' ? (
                            <>
                              <div className="w-24 h-24 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-12 h-12 text-white" />
                              </div>
                              <p className="text-2xl font-bold text-white mb-2">Face Mismatch</p>
                              <p className="text-red-400 text-lg">{faceMatchScore}% Similarity</p>
                            </>
                          ) : (
                            <>
                              <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
                                <CheckCircle2 className="w-12 h-12 text-white" />
                              </div>
                              <p className="text-2xl font-bold text-white mb-2">
                                {faceMatchStatus === 'verifying' ? 'Verifying...' : 'Face Matched!'}
                              </p>
                              <p className="text-green-400 text-lg">{faceMatchScore}% Similarity</p>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Step 5: Liveness check overlay */}
                    {currentStep === 5 && (
                      <div className="absolute inset-0 pointer-events-none">
                        {/* Refresh camera button - pointer events enabled */}
                        <button 
                          onClick={startCamera}
                          className="absolute top-4 left-4 bg-slate-700/80 hover:bg-slate-600 px-3 py-2 rounded-lg flex items-center gap-2 text-white text-sm transition-colors pointer-events-auto z-10"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                        
                        {/* Head position guide */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          <div className={`w-56 h-72 rounded-full border-4 transition-all duration-300 ${
                            livenessChecks.straight && livenessChecks.left && livenessChecks.right 
                              ? 'border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.5)]' 
                              : 'border-cyan-500/50'
                          }`}>
                            {/* Direction indicator */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className={`text-6xl transition-transform duration-300`} style={{ transform: `rotate(${-headPose.yaw}deg)` }}>
                                {currentLivenessAction === 'straight' ? '👁️' :
                                 currentLivenessAction === 'left' ? '👈' :
                                 currentLivenessAction === 'right' ? '👉' : '✅'}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Instructions */}
                        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
                          <div className={`px-6 py-3 rounded-full text-white font-medium ${
                            currentLivenessAction === 'done' ? 'bg-green-500' : 
                            'bg-cyan-500 animate-pulse'
                          }`}>
                            {currentLivenessAction === 'straight' ? '👀 Look straight at camera' :
                             currentLivenessAction === 'left' ? '👈 Turn your head LEFT' :
                             currentLivenessAction === 'right' ? '👉 Turn your head RIGHT' :
                             '✅ All checks complete!'}
                          </div>
                        </div>
                        
                        {/* Check boxes */}
                        <div className="absolute top-4 right-4 bg-slate-900/90 p-3 rounded-xl border border-slate-600 space-y-2">
                          {[
                            { key: 'straight', label: 'Look Straight', icon: '👁️' },
                            { key: 'left', label: 'Turn Left', icon: '👈' },
                            { key: 'right', label: 'Turn Right', icon: '👉' }
                          ].map(item => (
                            <div key={item.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                              livenessChecks[item.key] ? 'bg-green-500/20 border border-green-500' :
                              currentLivenessAction === item.key ? 'bg-cyan-500/20 border border-cyan-500 animate-pulse' :
                              'bg-slate-800 border border-slate-700'
                            }`}>
                              <span>{item.icon}</span>
                              <span className={`text-sm ${livenessChecks[item.key] ? 'text-green-400' : 'text-slate-400'}`}>
                                {item.label}
                              </span>
                              {livenessChecks[item.key] && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Step 6: Recording overlay */}
                    {currentStep === 6 && (
                      <div className="absolute inset-0 pointer-events-none">
                        {/* Recording indicator */}
                        {isRecording && (
                          <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500/90 px-4 py-2 rounded-full">
                            <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
                            <span className="text-white font-medium">REC</span>
                            <span className="text-white font-mono">
                              {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:
                              {String(recordingTime % 60).padStart(2, '0')}
                            </span>
                          </div>
                        )}
                        
                        {/* Face guide */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          <div className={`w-48 h-60 rounded-full border-2 transition-all ${
                            isRecording ? 'border-red-500' : 'border-cyan-500/30'
                          }`}>
                            {isRecording && (
                              <div className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-50" />
                            )}
                          </div>
                        </div>
                        
                        {/* Microphone indicator */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                            isRecording ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-300'
                          }`}>
                            <Mic className={`w-5 h-5 ${isRecording ? 'animate-pulse' : ''}`} />
                            <span>{isRecording ? 'Recording audio...' : 'Microphone ready'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Steps 1, 2: Info panel */}
            {(currentStep === 1 || currentStep === 2) && (
              <div className="aspect-video bg-slate-800 rounded-2xl border border-slate-700 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-20 h-20 rounded-full bg-lime-500/10 flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-10 h-10 text-lime-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Secure AI Verification</h3>
                  <p className="text-slate-400 max-w-md">AI-powered identity verification with face matching and liveness detection</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Form */}
          <div className="order-1 lg:order-2">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8">
                {/* Step 1 */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Verify Your Identity</h2>
                      <p className="text-slate-400">Enter the reference numbers from your invitation</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">University Reference Number *</Label>
                        <Input value={identityForm.university_reference_number} 
                          onChange={(e) => setIdentityForm({ ...identityForm, university_reference_number: e.target.value })}
                          placeholder="e.g., URN-2024-12345"
                          className="bg-slate-900 border-slate-600 text-white h-12" />
                      </div>
                      <div>
                        <Label className="text-slate-300">Passport Number</Label>
                        <Input value={identityForm.passport_number}
                          onChange={(e) => setIdentityForm({ ...identityForm, passport_number: e.target.value })}
                          placeholder="e.g., AB1234567"
                          className="bg-slate-900 border-slate-600 text-white h-12" />
                      </div>
                      <div>
                        <Label className="text-slate-300">NIC Number</Label>
                        <Input value={identityForm.nic_number}
                          onChange={(e) => setIdentityForm({ ...identityForm, nic_number: e.target.value })}
                          placeholder="e.g., 1234567890V"
                          className="bg-slate-900 border-slate-600 text-white h-12" />
                      </div>
                    </div>
                    <Button onClick={handleStep1} disabled={verifying} 
                      className="w-full h-12 bg-gradient-to-r from-lime-500 to-lime-600 hover:from-lime-600 hover:to-lime-700">
                      {verifying && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
                      Verify & Continue <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Step 2 */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Select University</h2>
                      <p className="text-slate-400">Confirm your university details</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">University *</Label>
                        <Select value={universityForm.university_id} onValueChange={(v) => setUniversityForm({ ...universityForm, university_id: v })}>
                          <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-12">
                            <SelectValue placeholder="Select university" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value={applicationData?.university_id} className="text-white">
                              {applicationData?.university_name}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button onClick={() => setCurrentStep(1)} variant="outline" className="flex-1 h-12 border-slate-600 text-slate-300">
                        <ArrowLeft className="w-5 h-5 mr-2" /> Back
                      </Button>
                      <Button onClick={handleStep2} disabled={verifying} 
                        className="flex-1 h-12 bg-gradient-to-r from-lime-500 to-lime-600">
                        {verifying && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
                        Continue <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3 - Document Upload */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Upload Identity Document</h2>
                      <p className="text-slate-400">We'll extract your face and verify document information</p>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Document Type</Label>
                        <Select value={documentType} onValueChange={(v) => { setDocumentType(v); setUploadedDocument(null); setDocumentVerification(null); setReferenceDescriptor(null); }}>
                          <SelectTrigger className="bg-slate-900 border-slate-600 text-white h-12">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="passport" className="text-white">Passport</SelectItem>
                            <SelectItem value="nic" className="text-white">National ID Card (NIC)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-lime-500 transition-colors cursor-pointer"
                        onClick={() => !isExtractingDocument && fileInputRef.current?.click()}>
                        {isExtractingDocument ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="w-12 h-12 text-lime-500 animate-spin mb-4" />
                            <p className="text-lime-400 font-medium">Analyzing document with AI...</p>
                            <p className="text-slate-500 text-sm mt-2">Extracting face and verifying information</p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                            <p className="text-white font-medium mb-2">Click to upload {documentType === 'passport' ? 'Passport' : 'NIC'}</p>
                            <p className="text-slate-500 text-sm">JPG, PNG up to 10MB</p>
                          </>
                        )}
                      </div>
                      
                      {/* Verification Results */}
                      {documentVerification && (
                        <div className="space-y-3">
                          {/* Face Extraction Status */}
                          <div className={`flex items-center gap-3 p-3 rounded-xl border ${referenceDescriptor ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            {referenceDescriptor ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
                            <span className={referenceDescriptor ? 'text-green-400' : 'text-red-400'}>
                              {referenceDescriptor ? 'Face extracted successfully' : 'Face extraction failed'}
                            </span>
                          </div>
                          
                          {/* Name Verification */}
                          <div className={`flex items-center justify-between p-3 rounded-xl border ${documentVerification.matching?.name?.match ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            <div className="flex items-center gap-3">
                              {documentVerification.matching?.name?.match ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
                              <div>
                                <p className={`font-medium ${documentVerification.matching?.name?.match ? 'text-green-400' : 'text-red-400'}`}>Name Verification</p>
                                <p className="text-xs text-slate-400">
                                  Expected: {documentVerification.matching?.name?.expected || 'N/A'}
                                </p>
                                <p className="text-xs text-slate-400">
                                  Found: {documentVerification.matching?.name?.extracted || 'Not detected'}
                                </p>
                              </div>
                            </div>
                            {documentVerification.matching?.name?.match && (
                              <span className="text-green-400 text-sm">{documentVerification.matching?.name?.score}%</span>
                            )}
                          </div>
                          
                          {/* Passport Number (if passport) */}
                          {documentType === 'passport' && documentVerification.matching?.passport_number?.expected && (
                            <div className={`flex items-center justify-between p-3 rounded-xl border ${documentVerification.matching?.passport_number?.match ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                              <div className="flex items-center gap-3">
                                {documentVerification.matching?.passport_number?.match ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
                                <div>
                                  <p className={`font-medium ${documentVerification.matching?.passport_number?.match ? 'text-green-400' : 'text-red-400'}`}>Passport Number</p>
                                  <p className="text-xs text-slate-400">
                                    Expected: {documentVerification.matching?.passport_number?.expected || 'N/A'}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    Found: {documentVerification.matching?.passport_number?.extracted || 'Not detected'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* NIC Number (if NIC) */}
                          {documentType === 'nic' && documentVerification.matching?.nic_number?.expected && (
                            <div className={`flex items-center justify-between p-3 rounded-xl border ${documentVerification.matching?.nic_number?.match ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                              <div className="flex items-center gap-3">
                                {documentVerification.matching?.nic_number?.match ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-red-500" />}
                                <div>
                                  <p className={`font-medium ${documentVerification.matching?.nic_number?.match ? 'text-green-400' : 'text-red-400'}`}>NIC Number</p>
                                  <p className="text-xs text-slate-400">
                                    Expected: {documentVerification.matching?.nic_number?.expected || 'N/A'}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    Found: {documentVerification.matching?.nic_number?.extracted || 'Not detected'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Overall Status */}
                          <div className={`p-4 rounded-xl border text-center ${documentVerification.document_verified ? 'bg-green-500/10 border-green-500' : 'bg-red-500/10 border-red-500'}`}>
                            {documentVerification.document_verified ? (
                              <>
                                <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                <p className="text-green-400 font-bold">Document Verified</p>
                                <p className="text-slate-400 text-sm">AI Confidence: {documentVerification.confidence}%</p>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                                <p className="text-red-400 font-bold">Verification Failed</p>
                                <p className="text-slate-400 text-sm">Please upload a valid document matching your application</p>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-4">
                      <Button onClick={() => setCurrentStep(2)} variant="outline" className="flex-1 h-12 border-slate-600 text-slate-300">
                        <ArrowLeft className="w-5 h-5 mr-2" /> Back
                      </Button>
                      <Button onClick={handleStep3} disabled={verifying || !referenceDescriptor || !documentVerification?.document_verified || isExtractingDocument}
                        className="flex-1 h-12 bg-gradient-to-r from-lime-500 to-lime-600 disabled:opacity-50">
                        {verifying && <Loader2 className="w-5 h-5 animate-spin mr-2" />}
                        Continue <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4 - Face Match */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Face Verification</h2>
                      <p className="text-slate-400">Position your face in the oval to match with your document</p>
                    </div>
                    
                    <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-4 h-4 rounded-full ${
                          faceMatchStatus === 'matched' ? 'bg-green-500' : 
                          faceMatchStatus === 'verifying' ? 'bg-blue-500 animate-pulse' :
                          faceMatchStatus === 'failed' ? 'bg-red-500' :
                          faceMatchStatus === 'matching' ? 'bg-yellow-500 animate-pulse' : 'bg-slate-600'}`} />
                        <span className="text-slate-300">
                          {faceMatchStatus === 'matched' ? 'Face Verified Successfully!' :
                           faceMatchStatus === 'verifying' ? 'AI is verifying your identity...' :
                           faceMatchStatus === 'failed' ? 'Verification failed - faces do not match' :
                           faceMatchStatus === 'matching' ? 'Capturing face...' :
                           faceMatchStatus === 'detecting' ? 'Detecting face...' : 'Waiting for camera...'}
                        </span>
                      </div>
                      
                      {faceMatchStatus === 'verifying' && (
                        <div className="flex items-center gap-2 text-blue-400 text-sm mb-3">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Verifying your identity...</span>
                        </div>
                      )}
                      
                      {faceMatchStatus === 'failed' && (
                        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg mb-3">
                          <p className="text-red-300 text-sm">
                            Face verification failed. This could be due to:
                          </p>
                          <ul className="text-red-300/80 text-xs mt-2 list-disc list-inside space-y-1">
                            <li>Different lighting conditions</li>
                            <li>Document photo quality</li>
                            <li>Significant appearance change</li>
                          </ul>
                          <p className="text-red-300 text-sm mt-2">
                            Click <strong>Re-upload Doc</strong> to try a different document, or click the <strong>refresh</strong> button to retry.
                          </p>
                        </div>
                      )}
                      
                      {faceMatchScore !== null && faceMatchStatus !== 'verifying' && (
                        <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full transition-all duration-500 ${
                            faceMatchScore >= 70 ? 'bg-green-500' : faceMatchScore >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`} style={{ width: `${faceMatchScore}%` }} />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-4">
                      <Button onClick={() => { 
                        // Reset all face matching related states
                        setCurrentStep(3); 
                        setUploadedDocument(null); 
                        setReferenceDescriptor(null);
                        setReferenceFaceImage(null);
                        setCapturedSelfie(null);
                        setFaceMatchStatus('waiting');
                        setFaceMatchScore(null);
                        setFacePosition('none');
                        setDocumentVerification(null);
                        clearAllIntervals();
                        // Clear any pending timeouts
                        if (autoMatchTimeoutRef.current) {
                          clearTimeout(autoMatchTimeoutRef.current);
                          autoMatchTimeoutRef.current = null;
                        }
                        toast.info('Please upload a new document');
                      }}
                        variant="outline" className="flex-1 h-12 border-slate-600 text-slate-300">
                        <ArrowLeft className="w-5 h-5 mr-2" /> Re-upload Doc
                      </Button>
                      <Button onClick={() => {
                        // Just retry face matching without re-uploading
                        setCapturedSelfie(null);
                        setFaceMatchStatus('detecting');
                        setFaceMatchScore(null);
                        if (autoMatchTimeoutRef.current) {
                          clearTimeout(autoMatchTimeoutRef.current);
                          autoMatchTimeoutRef.current = null;
                        }
                        toast.info('Retrying face verification...');
                      }} variant="outline" className="h-12 border-slate-600 text-slate-300">
                        <RefreshCw className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 5 - Liveness */}
                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Liveness Check</h2>
                      <p className="text-slate-400">Follow the instructions - movements are detected automatically</p>
                    </div>
                    
                    <div className="space-y-3">
                      {[
                        { key: 'straight', label: 'Look straight at camera', icon: '👁️' },
                        { key: 'left', label: 'Turn your head LEFT', icon: '👈' },
                        { key: 'right', label: 'Turn your head RIGHT', icon: '👉' }
                      ].map(item => (
                        <div key={item.key} className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                          livenessChecks[item.key] ? 'bg-green-500/20 border border-green-500' :
                          currentLivenessAction === item.key ? 'bg-cyan-500/20 border-2 border-cyan-500 animate-pulse' :
                          'bg-slate-900/50 border border-slate-700'
                        }`}>
                          <span className="text-2xl">{item.icon}</span>
                          <span className={livenessChecks[item.key] ? 'text-green-400' : 'text-slate-300'}>{item.label}</span>
                          {livenessChecks[item.key] && <CheckCircle2 className="w-6 h-6 text-green-500 ml-auto" />}
                        </div>
                      ))}
                    </div>
                    
                    {livenessChecks.straight && livenessChecks.left && livenessChecks.right && (
                      <div className="p-4 bg-green-500/20 border border-green-500 rounded-xl text-center">
                        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                        <p className="text-green-400 font-medium">All checks complete! Proceeding...</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 6 - Consent Recording */}
                {currentStep === 6 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="font-bold text-2xl text-white mb-2">Record Consent Statement</h2>
                      <p className="text-slate-400">Read the statement aloud while being recorded (30 seconds)</p>
                    </div>
                    
                    {/* Statement to read */}
                    <div className="bg-slate-900/80 rounded-xl p-5 border-2 border-cyan-500/50 relative">
                      <div className="absolute -top-3 left-4 bg-slate-900 px-2">
                        <span className="text-cyan-400 text-sm font-medium">📜 Read This Statement Aloud</span>
                      </div>
                      <p className="text-white leading-relaxed text-sm mt-2">
                        {CONSENT_STATEMENT}
                      </p>
                    </div>
                    
                    {/* Recording status */}
                    <div className={`p-4 rounded-xl border transition-all ${
                      isRecording ? 'bg-red-500/10 border-red-500 animate-pulse' :
                      recordedBlob ? 'bg-green-500/10 border-green-500' :
                      'bg-slate-900/50 border-slate-700'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isRecording ? (
                            <>
                              <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                              <span className="text-red-400 font-medium">Recording...</span>
                            </>
                          ) : recordedBlob ? (
                            <>
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                              <span className="text-green-400 font-medium">Recording Complete</span>
                            </>
                          ) : (
                            <>
                              <Video className="w-5 h-5 text-slate-400" />
                              <span className="text-slate-400">Ready to record</span>
                            </>
                          )}
                        </div>
                        
                        {/* Timer */}
                        <div className={`text-2xl font-mono font-bold ${
                          isRecording ? 'text-red-400' : 'text-slate-500'
                        }`}>
                          {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:
                          {String(recordingTime % 60).padStart(2, '0')}
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      {isRecording && (
                        <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500 transition-all duration-1000"
                            style={{ width: `${((30 - recordingTime) / 30) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Recording controls */}
                    <div className="flex gap-4">
                      {!isRecording && !recordedBlob && (
                        <Button 
                          onClick={startRecording}
                          className="flex-1 h-12 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                        >
                          <Play className="w-5 h-5 mr-2" />
                          Start Recording
                        </Button>
                      )}
                      
                      {isRecording && (
                        <Button 
                          onClick={stopRecording}
                          variant="outline"
                          className="flex-1 h-12 border-red-500 text-red-400 hover:bg-red-500/10"
                        >
                          <Square className="w-5 h-5 mr-2" />
                          Stop Recording
                        </Button>
                      )}
                      
                      {recordedBlob && !uploadComplete && (
                        <>
                          <Button 
                            onClick={() => { setRecordedBlob(null); setRecordingTime(30); }}
                            variant="outline"
                            className="h-12 border-slate-600 text-slate-300"
                          >
                            <RefreshCw className="w-5 h-5 mr-2" />
                            Re-record
                          </Button>
                          <Button 
                            onClick={uploadAndProceed}
                            disabled={isUploading}
                            className="flex-1 h-12 bg-gradient-to-r from-lime-500 to-lime-600"
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-5 h-5 mr-2" />
                                Upload & Enter Room
                              </>
                            )}
                          </Button>
                        </>
                      )}
                      
                      {uploadComplete && (
                        <div className="flex-1 h-12 bg-green-500/20 border border-green-500 rounded-lg flex items-center justify-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <span className="text-green-400 font-medium">Uploaded! Entering interview room...</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Instructions */}
                    <div className="text-sm text-slate-500 space-y-1">
                      <p>• Ensure your microphone is working and speak clearly</p>
                      <p>• The recording will be uploaded to secure storage</p>
                      <p>• You have 30 seconds to read the complete statement</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes scan {
          0% { top: 0; }
          100% { top: 100%; }
        }
        .animate-scan {
          animation: scan 2s linear infinite;
        }
      `}</style>
    </div>
  );
}
